/**
 * Appointment Status Update API Integration
 * 
 * This module provides comprehensive API integration for appointment status updates,
 * including hooks, validation, error handling, and batch operations.
 */

import { AppointmentStatus, AppointmentStatusManager, migrateStatus, mapStatusToBackend } from '../types/appointment-status';

export interface StatusUpdateRequest {
  appointmentId: string; // Use string to support both numeric and UUID formats
  newStatus: AppointmentStatus;
  reason?: string;
  notes?: string;
  notifyPatient?: boolean;
}

export interface StatusUpdateResponse {
  success: boolean;
  appointmentId: string;
  previousStatus: AppointmentStatus;
  newStatus: AppointmentStatus;
  timestamp: string;
  error?: string;
  warnings?: string[];
}

export interface BatchStatusUpdateRequest {
  updates: StatusUpdateRequest[];
}

export interface BatchStatusUpdateResponse {
  totalRequested: number;
  successful: number;
  failed: number;
  results: StatusUpdateResponse[];
  errors: string[];
}

export interface StatusHistoryEntry {
  id: number;
  appointmentId: string; // Changed to string for UUID support
  previousStatus: AppointmentStatus;
  newStatus: AppointmentStatus;
  changedBy: string;
  changedAt: string;
  reason?: string;
  notes?: string;
  isAutomatic: boolean;
}

/**
 * Helper function to validate appointment ID format
 */
function validateAppointmentId(id: string): void {
  if (!id || typeof id !== 'string' || id.trim().length === 0) {
    throw new ValidationError(`Invalid appointment ID: ${id}`);
  }
}

/**
 * API Error Types
 */
export class StatusUpdateError extends Error {
  constructor(
    message: string,
    public appointmentId?: string,
    public currentStatus?: AppointmentStatus,
    public requestedStatus?: AppointmentStatus,
    public code?: string
  ) {
    super(message);
    this.name = 'StatusUpdateError';
  }
}

export class ValidationError extends StatusUpdateError {
  constructor(message: string, appointmentId?: string) {
    super(message, appointmentId);
    this.name = 'ValidationError';
    this.code = 'VALIDATION_ERROR';
  }
}

export class TransitionError extends StatusUpdateError {
  constructor(from: AppointmentStatus, to: AppointmentStatus, appointmentId?: string) {
    super(`Invalid status transition from '${from}' to '${to}'`, appointmentId, from, to);
    this.name = 'TransitionError';
    this.code = 'INVALID_TRANSITION';
  }
}

/**
 * CSRF Token Utility
 */
function getCsrfToken(): string | null {
  // Try to get CSRF token from cookies
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'csrftoken') {
      return decodeURIComponent(value);
    }
  }
  
  // Fallback: try to get from meta tag
  const metaTag = document.querySelector('meta[name="csrf-token"]');
  if (metaTag) {
    return metaTag.getAttribute('content');
  }
  
  return null;
}

function getApiHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  };
  
  const csrfToken = getCsrfToken();
  if (csrfToken) {
    headers['X-CSRFToken'] = csrfToken;
  }
  
  return headers;
}

/**
 * Main Appointment Status Service
 */
export class AppointmentStatusService {
  private static readonly baseUrl = '/api/appointments';

  /**
   * Update a single appointment status
   */
  static async updateStatus(request: StatusUpdateRequest): Promise<StatusUpdateResponse> {
    try {
      // Validate appointment ID
      validateAppointmentId(request.appointmentId);
      
      // Client-side validation
      this.validateStatusUpdate(request);

      const backendStatus = mapStatusToBackend(request.newStatus);
      console.log(`🔄 Updating appointment ${request.appointmentId}: ${request.newStatus} → ${backendStatus}`);

      const response = await fetch(`${this.baseUrl}/${request.appointmentId}/update/`, {
        method: 'PUT',
        credentials: 'include',
        headers: getApiHeaders(),
        body: JSON.stringify({
          status: backendStatus,
          reason: request.reason,
          notes: request.notes,
          notify_patient: request.notifyPatient || false
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new StatusUpdateError(
          errorData.error || `HTTP ${response.status}: ${response.statusText}`,
          request.appointmentId,
          undefined,
          request.newStatus,
          errorData.code
        );
      }

      const data = await response.json();
      console.log(`✅ Status update response:`, data);
      
      return {
        success: true,
        appointmentId: request.appointmentId,
        previousStatus: migrateStatus(data.previous_status || 'pending'),
        newStatus: request.newStatus,
        timestamp: data.updated_at || new Date().toISOString(),
        warnings: data.warnings
      };

    } catch (error) {
      if (error instanceof StatusUpdateError) {
        throw error;
      }

      throw new StatusUpdateError(
        `Failed to update appointment ${request.appointmentId}: ${error instanceof Error ? error.message : 'Unknown error'}`,
        request.appointmentId,
        undefined,
        request.newStatus
      );
    }
  }

  /**
   * Update multiple appointment statuses in batch
   */
  static async updateStatusBatch(request: BatchStatusUpdateRequest): Promise<BatchStatusUpdateResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/status/batch/`, {
        method: 'PUT',
        credentials: 'include',
        headers: getApiHeaders(),
        body: JSON.stringify({
          updates: request.updates.map(update => ({
            appointment_id: update.appointmentId,
            status: update.newStatus,
            reason: update.reason,
            notes: update.notes,
            notify_patient: update.notifyPatient || false
          }))
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new StatusUpdateError(
          errorData.error || `Batch update failed: HTTP ${response.status}`,
          undefined,
          undefined,
          undefined,
          errorData.code
        );
      }

      const data = await response.json();
      
      return {
        totalRequested: request.updates.length,
        successful: data.successful || 0,
        failed: data.failed || 0,
        results: (data.results || []).map((result: any) => ({
          success: result.success,
          appointmentId: result.appointment_id,
          previousStatus: migrateStatus(result.previous_status || 'pending'),
          newStatus: migrateStatus(result.new_status || 'pending'),
          timestamp: result.updated_at || new Date().toISOString(),
          error: result.error,
          warnings: result.warnings
        })),
        errors: data.errors || []
      };

    } catch (error) {
      if (error instanceof StatusUpdateError) {
        throw error;
      }

      throw new StatusUpdateError(
        `Batch status update failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Get status history for an appointment
   */
  static async getStatusHistory(appointmentId: string): Promise<StatusHistoryEntry[]> {
    try {
      validateAppointmentId(appointmentId);

      const response = await fetch(`${this.baseUrl}/${appointmentId}/status/history/`, {
        credentials: 'include',
        headers: getApiHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new StatusUpdateError(
          errorData.error || `Failed to load status history: HTTP ${response.status}`,
          appointmentId
        );
      }

      const data = await response.json();
      
      return (data.history || []).map((entry: any) => ({
        id: entry.id,
        appointmentId: entry.appointment_id,
        previousStatus: migrateStatus(entry.previous_status || 'pending'),
        newStatus: migrateStatus(entry.new_status || 'pending'),
        changedBy: entry.changed_by || 'Unknown',
        changedAt: entry.changed_at || new Date().toISOString(),
        reason: entry.reason,
        notes: entry.notes,
        isAutomatic: entry.is_automatic || false
      }));

    } catch (error) {
      if (error instanceof StatusUpdateError) {
        throw error;
      }

      throw new StatusUpdateError(
        `Failed to load status history for appointment ${appointmentId}: ${error instanceof Error ? error.message : 'Unknown error'}`,
        appointmentId
      );
    }
  }

  /**
   * Check and update overdue appointments automatically
   */
  static async checkAndUpdateOverdueAppointments(): Promise<{
    checked: number;
    updated: number;
    errors: string[];
  }> {
    try {
      const response = await fetch(`${this.baseUrl}/status/check-overdue/`, {
        method: 'POST',
        credentials: 'include',
        headers: getApiHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new StatusUpdateError(
          errorData.error || `Failed to check overdue appointments: HTTP ${response.status}`,
          undefined,
          undefined,
          undefined,
          errorData.code
        );
      }

      const data = await response.json();
      
      return {
        checked: data.checked || 0,
        updated: data.updated || 0,
        errors: data.errors || []
      };

    } catch (error) {
      if (error instanceof StatusUpdateError) {
        throw error;
      }

      throw new StatusUpdateError(
        `Failed to check overdue appointments: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Client-side validation before API call
   */
  private static validateStatusUpdate(request: StatusUpdateRequest): void {
    validateAppointmentId(request.appointmentId);
    
    if (!request.newStatus) {
      throw new ValidationError('New status is required', request.appointmentId);
    }

    // Note: We can't validate current status without fetching it first,
    // so transition validation will be done on the server side
  }

  /**
   * Validate status transition client-side if current status is known
   */
  static validateTransition(currentStatus: AppointmentStatus, newStatus: AppointmentStatus): boolean {
    return AppointmentStatusManager.canTransition(currentStatus, newStatus);
  }

  /**
   * Get available status options for current status
   */
  static getAvailableTransitions(currentStatus: AppointmentStatus): AppointmentStatus[] {
    return AppointmentStatusManager.getValidTransitions(currentStatus);
  }
}

/**
 * React Hooks for Status Management
 */
import { useState, useCallback } from 'react';

export function useAppointmentStatusUpdate() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateStatus = useCallback(async (request: StatusUpdateRequest): Promise<StatusUpdateResponse> => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await AppointmentStatusService.updateStatus(request);
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update appointment status';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    updateStatus,
    loading,
    error,
    clearError: () => setError(null)
  };
}

export function useAppointmentStatusBatch() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateStatusBatch = useCallback(async (request: BatchStatusUpdateRequest): Promise<BatchStatusUpdateResponse> => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await AppointmentStatusService.updateStatusBatch(request);
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update appointment statuses';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    updateStatusBatch,
    loading,
    error,
    clearError: () => setError(null)
  };
}

export function useAppointmentStatusHistory(appointmentId: string) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<StatusHistoryEntry[]>([]);

  const loadHistory = useCallback(async () => {
    if (!appointmentId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await AppointmentStatusService.getStatusHistory(appointmentId);
      setHistory(result);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load status history';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [appointmentId]);

  return {
    history,
    loading,
    error,
    loadHistory,
    clearError: () => setError(null)
  };
}

export function useOverdueAppointmentMonitoring() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastCheck, setLastCheck] = useState<{
    timestamp: string;
    checked: number;
    updated: number;
    errors: string[];
  } | null>(null);

  const checkOverdueAppointments = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await AppointmentStatusService.checkAndUpdateOverdueAppointments();
      setLastCheck({
        timestamp: new Date().toISOString(),
        ...result
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to check overdue appointments';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    checkOverdueAppointments,
    loading,
    error,
    lastCheck,
    clearError: () => setError(null)
  };
}

// Default export
export default AppointmentStatusService;